import IconComponent from "@CubeComponents/icons";
import Popover from "@mui/material/Popover";
import { useState } from "react";
import PropTypes from "prop-types";
import { maximiseFileSync } from "@Actions/filesync";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { CircularProgress, Link, ButtonBase } from "@mui/material";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import RemoveSyncModal from "@Components/FilesSync/RemoveSyncModal";
function FolderSyncTemplate({ spaceId, projectId, spaceData, currentFolder }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  function handleClick(event) {
    if (countOfLinkFiles == 0) {
      initLocalFoldersSync();
    } else {
      setAnchorEl(event.currentTarget);
    }
  }

  const handleCloseOverlay = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const [removeSync, setRemoveSync] = useState({
    modal: false,
    data: null,
  });
  const dispatch = useDispatch();
  const fileSyncState = useSelector((state) => state.fileSyncState);
  const fileSyncMapperState = useSelector((state) => state.fileSyncMapperState);

  function buildSpaceHierarchyMap(input) {
    let items = [];
    if (Array.isArray(input)) {
      items = input;
    } else if (input?.data?.state && Array.isArray(input.data.state)) {
      items = input.data.state;
    }

    const res = {};
    for (let i = 0; i < items.length; i++) {
      let raw = items[i];
      if (!raw || typeof raw !== "object") continue;

      const spaceId = String(raw.spaceId || "");
      const hierarchyId = String(raw.hierarchyId || "");
      if (!spaceId || !hierarchyId) continue;

      if (!res[spaceId]) {
        res[spaceId] = {};
      }
      if (!res[spaceId][hierarchyId]) {
        res[spaceId][hierarchyId] = { data: [] };
      }
      res[spaceId][hierarchyId].data.push({ ...raw, index: i });
    }
    return res;
  }

  const handlePause = (index) => {
    window.electronAPI.pauseFileSync({ index });
  };
  const handleResume = (index) => {
    window.electronAPI.resumeFileSync({ index });
  };
  const handleCancel = (index) => {
    window.electronAPI.cancelFileSync({ index });
  };

  const spaceMap = buildSpaceHierarchyMap(fileSyncState);
  const spaceFileSyncData = spaceMap[spaceId] || {};

  const countOfLinkFiles = Object.values(spaceFileSyncData)
    .flatMap((o) => o.data)
    .filter((d) => d.status !== "CANCELLED").length;

  function initLocalFoldersSync() {
    window.electronAPI.initLocalFoldersSync({
      spaceId,
      spaceName: spaceData.spaceName,
      path: currentFolder.name,
      projectId: projectId,
      hierarchyId: currentFolder.hierarchyId,
      origin: window.location.origin,
    });
  }

  const renderSyncItem = (item) => {
    if (item.status === "CANCELLED") return null;

    return (
      <div className="flex flex-row items-center gap-6 py-1 justify-between" key={item.watchedPath}>
        <div className="flex flex-row items-center gap-6 overflow-auto">
          <IconComponent name="desktop" className="w-6 h-6" />
          <div className="overflow-hidden truncate">
            <ToolTipComponent content={item.watchedPath || ""}>
              <div className="overflow-hidden truncate">{item.watchedPath}</div>
            </ToolTipComponent>
          </div>
        </div>
        <div className="flex flex-row items-center justify-center">
          {item.status === "IN_PROGRESS" && (
            <div className="flex group relative w-6 h-6 justify-center items-center">
              <div className="group-hover:hidden flex justify-center items-center">
                <CircularProgress size={16} thickness={6} />
              </div>
              <div
                aria-label="Pause Sync"
                className="hidden group-hover:flex items-center justify-center cursor-pointer"
              >
                <IconComponent name="PauseIcon" className="w-5 h-5" onClick={() => handlePause(item.index)} />
              </div>
            </div>
          )}

          {item.status === "PAUSED" && (
            <div className="flex justify-center flex-row items-center gap-6">
              <IconComponent
                name="Start"
                aria-label="Resume Sync"
                className="w-4 h-4 cursor-pointer"
                onClick={() => handleResume(item.index)}
              />
              <IconComponent
                name="Reject"
                aria-label="Remove Sync"
                className="w-4 h-4 cursor-pointer"
                onClick={() => setRemoveSync({ modal: true, data: item.index })}
              />
            </div>
          )}

          {item.status === "ERRORED" && (
            <div className="flex justify-center flex-row items-center gap-6">
              <ToolTipComponent content="Something went wrong while adding remote file">
                <IconComponent name="Error" className="w-5 h-5" />
              </ToolTipComponent>
              <IconComponent
                name="Reject"
                aria-label="Remove Sync"
                className="w-4 h-4 cursor-pointer"
                onClick={() => setRemoveSync({ modal: true, data: item.index })}
              />
            </div>
          )}

          {(item.status === "COMPLETED" || !item.status) && (
            <div className="group relative w-6 h-6 flex justify-center items-center">
              <div className="group-hover:hidden">
                <IconComponent name="TickRight" className="w-5 h-5" />
              </div>
              <div
                aria-label="Remove Sync"
                className="hidden group-hover:flex items-center justify-center cursor-pointer"
              >
                <IconComponent
                  name="Reject"
                  className="w-4 h-4"
                  onClick={() => setRemoveSync({ modal: true, data: item.index })}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <ButtonBase aria-describedby={id} className="flex gap-2 flex-row p-1" onClick={handleClick}>
        {countOfLinkFiles === 0 ? (
          <>
            <IconComponent name="desktop" className="w-4 h-4" />
            <div className="font-semibold text-sm">{t("Sync this Folder")}</div>
          </>
        ) : (
          <>
            <IconComponent name="consume" className="w-4 h-4" />
            <div className="font-semibold text-sm">{t("Syncing with PC")}</div>
            <div className="rounded-full bg-button_light font-semibold text-sm w-6 h-6 flex truncate items-center justify-center">
              {countOfLinkFiles}
            </div>
          </>
        )}
      </ButtonBase>
      <Popover
        className="z-10"
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseOverlay}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <div className="w-120 max-h-200 flex">
          <div className="flex flex-col gap-6 p-6 overflow-auto w-full">
            <div className="flex flex-row justify-between items-center">
              <ButtonComponent onClick={initLocalFoldersSync}>
                <div className="flex flex-row gap-2 items-center">
                  <IconComponent name="add" className="w-4 h-4" />
                  <div>{t("New")}</div>
                </div>
              </ButtonComponent>
              <Link
                underline="hover"
                className="text-accentBlue font-semibold cursor-pointer text-sm"
                onClick={() => {
                  handleCloseOverlay();
                  dispatch(maximiseFileSync());
                }}
              >
                {t("View All Syncs")}
              </Link>
            </div>

            <div className="flex flex-col gap-6 overflow-auto">
              {Object.keys(spaceFileSyncData).map((key) => {
                const currSpaceData = spaceFileSyncData[key];
                const spaceDataLength = _.get(currSpaceData, "data", []).filter(
                  (item) => item.status !== "CANCELLED",
                ).length;

                const folderName =
                  key === "0"
                    ? spaceData.spaceName
                    : _.get(fileSyncMapperState, `hierarchyId[${key}].data.filterPath`, "").split("/").pop();

                if (spaceDataLength === 0) return null;

                return (
                  <div className="flex flex-col gap-6" key={key}>
                    <div className="flex flex-row items-center gap-2">
                      <IconComponent name="folder" className="w-6 h-6" />
                      <Trans
                        i18nKey="folder_sync_with"
                        values={{
                          folderName,
                        }}
                        components={{
                          div1: <div style={{ fontSize: "1rem", fontWeight: "bold" }} />,
                          div2: <div style={{ fontSize: "1rem" }} />,
                        }}
                      />
                    </div>
                    <div className="flex flex-col gap-6 border-button_light border rounded-lg px-4 py-6">
                      {_.get(currSpaceData, "data", []).map(renderSyncItem)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <RemoveSyncModal removeSync={removeSync} setRemoveSync={setRemoveSync} handleCancel={handleCancel} />
        </div>
      </Popover>
    </>
  );
}

FolderSyncTemplate.propTypes = {
  spaceId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  spaceData: PropTypes.shape({
    spaceName: PropTypes.string,
  }),
  currentFolder: PropTypes.shape({
    name: PropTypes.string,
    hierarchyId: PropTypes.string,
  }),
};

export default FolderSyncTemplate;
